import { clearVmsSession, getVmsToken } from "../utils/auth";

const API_BASE = process.env.REACT_APP_BASE_URL || "";
const withBase = (path) => `${API_BASE}${path}`;
const VMS_SESSION_EXPIRED_MESSAGE = "Session expired, please login again.";

const redirectToVmsLogin = () => {
  if (typeof window === "undefined") return;
  const currentPath = window.location.pathname || "";
  const isAlreadyOnVmsAuthPage =
    currentPath === "/vms/login" ||
    currentPath === "/vms/forgot-password" ||
    currentPath === "/vms/reset-password";
  if (!isAlreadyOnVmsAuthPage) {
    window.location.replace("/vms/login");
  }
};

const normalizeError = async (response) => {
  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  const message =
    payload?.message ||
    payload?.detail ||
    `Request failed with status ${response.status}`;

  const error = new Error(message);
  error.status = response.status;
  error.payload = payload;
  return error;
};

const request = async (path, options = {}) => {
  const { skipAuth = false, ...fetchOptions } = options;
  const token = getVmsToken();
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
    ...(fetchOptions.headers || {}),
  };

  if (token && !skipAuth) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(withBase(path), {
    ...fetchOptions,
    headers,
  });

  if (!response.ok) {
    const err = await normalizeError(response);
    if (err.status === 401) {
      clearVmsSession();
      err.message = VMS_SESSION_EXPIRED_MESSAGE;
      redirectToVmsLogin();
    }
    throw err;
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
};

export const vmsApi = {
  setupAdmin: (payload) =>
    request("/vms/setup/admin", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  login: (payload) =>
    request("/vms/auth/login", {
      method: "POST",
      body: JSON.stringify({
        ...payload,
        username: payload?.username || payload?.email,
      }),
    }),
  forgotPassword: (email) =>
    request("/vms/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  resetPassword: (payload) =>
    request("/vms/auth/reset-password", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  changePassword: (payload) =>
    request("/vms/auth/change-password", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  runDailyMaintenance: () =>
    request("/vms/admin/maintenance/daily-run", {
      method: "POST",
    }),
  createOffice: (payload) =>
    request("/vms/admin/offices", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  getOffices: async () => {
    const res = await request("/vms/offices");
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.offices)) return res.offices;
    if (Array.isArray(res?.data)) return res.data;
    return [];
  },
  getGates: async () => {
    const res = await request("/vms/gates", { skipAuth: true });
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.gates)) return res.gates;
    if (Array.isArray(res?.data)) return res.data;
    return [];
  },
  getOfficeBookingLink: (params = {}) => {
    const search = new URLSearchParams();
    if (params.office_id) search.set("office_id", params.office_id);
    if (params.person_id) search.set("person_id", params.person_id);
    const query = search.toString();
    return request(`/vms/office/booking-link${query ? `?${query}` : ""}`);
  },
  createGate: (payload) =>
    request("/vms/admin/gates", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  deleteGate: (gateId) =>
    request(`/vms/admin/gates/${gateId}`, {
      method: "DELETE",
    }),
  createPerson: (officeId, payload) =>
    request(`/vms/admin/offices/${officeId}/persons`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updatePerson: (personId, payload) =>
    request(`/vms/admin/persons/${personId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  getPersons: (officeId) => request(`/vms/offices/${officeId}/persons`),
  getPublicOfficePersons: (officeId) => request(`/vms/offices/${officeId}/persons`, { skipAuth: true }),
  visitorSignIn: (payload) =>
    request("/vms/visitor/sign-in", {
      method: "POST",
      body: JSON.stringify(payload),
      skipAuth: true,
    }),
  listPersons: (params = {}) => {
    const search = new URLSearchParams();
    if (params.office_id) search.set("office_id", params.office_id);
    if (params.status) search.set("status", params.status);
    if (params.search) search.set("search", params.search);
    const query = search.toString();
    return request(`/vms/persons${query ? `?${query}` : ""}`);
  },
  createOfficeUser: (payload) =>
    request("/vms/admin/users", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  createSecurityUsers: (emails = []) =>
    request("/vms/admin/security-users", {
      method: "POST",
      body: JSON.stringify({ emails }),
    }),
  listSecurityUsers: () => request("/vms/admin/security-users"),
  updateSecurityUserStatus: (userId, action) =>
    request(`/vms/admin/security-users/${userId}/status?action=${action}`, {
      method: "PATCH",
    }),
  deleteSecurityUser: (userId) =>
    request(`/vms/admin/security-users/${userId}`, {
      method: "DELETE",
    }),
  listOfficeUsers: (params = {}) => {
    const search = new URLSearchParams();
    if (params.office_id) search.set("office_id", params.office_id);
    if (params.status) search.set("status", params.status);
    const query = search.toString();
    return request(`/vms/admin/users${query ? `?${query}` : ""}`);
  },
  updateOfficeUserStatus: (userId, action) =>
    request(`/vms/admin/users/${userId}/status?action=${action}`, {
      method: "PATCH",
    }),
  deleteOfficeUser: (userId) =>
    request(`/vms/admin/users/${userId}`, {
      method: "DELETE",
    }),
  deletePerson: (personId) =>
    request(`/vms/admin/persons/${personId}`, {
      method: "DELETE",
    }),
  getRequests: (params = {}) => {
    const search = new URLSearchParams();
    if (params.office_id) search.set("office_id", params.office_id);
    if (params.status) search.set("status", params.status);
    if (params.start_date) search.set("start_date", params.start_date);
    if (params.end_date) search.set("end_date", params.end_date);
    if (params.open_only === true || params.open_only === "true") search.set("open_only", "true");
    const page = Math.max(1, Number(params.page) || 1);
    const pageSize = Math.min(200, Math.max(1, Number(params.page_size) || 20));
    search.set("page", String(page));
    search.set("page_size", String(pageSize));
    const query = search.toString();
    return request(`/vms/requests?${query}`);
  },
  decideRequest: (requestCode, payload) =>
    request(`/vms/requests/${requestCode}/decision`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  getVisitsReport: (params = {}) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        search.set(key, value);
      }
    });
    const query = search.toString();
    return request(`/vms/reports/visits${query ? `?${query}` : ""}`);
  },
  getSummaryReport: (params = {}) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        search.set(key, value);
      }
    });
    const query = search.toString();
    return request(`/vms/reports/summary${query ? `?${query}` : ""}`);
  },
  getActivityLog: (params = {}) => {
    const search = new URLSearchParams();
    if (params.actor_email) search.set("actor_email", params.actor_email);
    if (params.actor_role) search.set("actor_role", params.actor_role);
    if (params.action) search.set("action", params.action);
    if (params.start_date) search.set("start_date", params.start_date);
    if (params.end_date) search.set("end_date", params.end_date);
    const page = Math.max(1, Number(params.page) || 1);
    const pageSize = Math.min(200, Math.max(1, Number(params.page_size) || 20));
    search.set("page", String(page));
    search.set("page_size", String(pageSize));
    return request(`/vms/admin/activity-log?${search.toString()}`);
  },
  getExportReport: async (params = {}) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        search.set(key, value);
      }
    });
    const query = search.toString();
    const token = getVmsToken();
    const res = await fetch(withBase(`/vms/reports/export${query ? `?${query}` : ""}`), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!res.ok) {
      const err = await normalizeError(res);
      if (err.status === 401) {
        clearVmsSession();
        err.message = VMS_SESSION_EXPIRED_MESSAGE;
        redirectToVmsLogin();
      }
      throw err;
    }
    const contentType = res.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      return res.json();
    }
    if (contentType.includes("text/csv") || contentType.includes("text/plain")) {
      return res.text();
    }
    return res.blob();
  },
};
